import { supabase } from '../supabaseClient';

export const SUPPORT_TICKET_STATUSES = {
  OPEN: 'Open',
  PENDING: 'Pending',
  RESOLVED: 'Resolved',
};

export const SUPPORT_TICKET_SOURCES = {
  BOOKING_APP: 'booking_app',
  WEBSITE: 'website',
};

export const WEBSITE_ISSUE_CATEGORIES = [
  'Ticket Issue',
  'Payment Problem',
  'Refund Request',
  'Technical Error',
  'Other',
];

export const REFUND_REQUEST_ISSUE_TYPE = 'Refund Request';

export const isRefundRequestType = (issueType) => (
  String(issueType || '').trim() === REFUND_REQUEST_ISSUE_TYPE
);

export const BOOKING_ISSUE_TYPES = [
  'Account issue',
  'Booking concern',
  'Payment problem',
  'Technical issue',
  'Other',
];

const MISSING_SCHEMA_MESSAGE =
  'Support tickets are not set up yet. Run create_support_tickets.sql in the Supabase SQL editor, then try again.';

export const generateSupportTicketCode = () => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `SUP-${timestamp}-${suffix}`;
};

export const isOngoingSupportStatus = (status) => {
  const normalized = String(status || '').trim();
  return normalized === SUPPORT_TICKET_STATUSES.OPEN || normalized === SUPPORT_TICKET_STATUSES.PENDING;
};

export const isClosedSupportStatus = (status) => (
  String(status || '').trim() === SUPPORT_TICKET_STATUSES.RESOLVED
);

export const groupSupportTicketsByTab = (tickets = []) => ({
  ongoing: tickets.filter((ticket) => isOngoingSupportStatus(ticket.status)),
  closed: tickets.filter((ticket) => isClosedSupportStatus(ticket.status)),
});

export const supportTicketSubject = (ticket = {}) => {
  const subject = String(ticket.subject || '').trim();
  if (subject) return subject;
  return String(ticket.issue_type || ticket.issueType || 'Support request').trim() || 'Support request';
};

export const isMissingSupportTicketsSchemaError = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '').toLowerCase();

  if (code === '42P01' || code === 'PGRST205' || code === '42703') return true;
  if (message.includes('support_tickets') && (
    message.includes('does not exist')
    || message.includes('schema cache')
    || message.includes('could not find')
    || message.includes('relation')
  )) {
    return true;
  }

  return false;
};

export const formatSupportTicketError = (error, fallback = 'Unable to save your support ticket.') => {
  if (isMissingSupportTicketsSchemaError(error)) {
    return MISSING_SCHEMA_MESSAGE;
  }

  const message = String(error?.message || error || '').trim();
  if (!message) return fallback;
  if (isMissingSupportTicketsSchemaError({ message })) return MISSING_SCHEMA_MESSAGE;
  return message;
};

const resolveRequesterProfile = async (user) => {
  let email = String(user?.email || '').trim();
  let name = String(user?.user_metadata?.full_name || user?.user_metadata?.name || '').trim();

  if (user?.id) {
    const { data } = await supabase
      .from('users')
      .select('email, full_name')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      email = String(data.email || email).trim();
      name = String(data.full_name || name).trim();
    }
  }

  if (!name && email) {
    name = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
  }

  return { email, name };
};

const requireSignedInUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    throw new Error(error.message || 'Unable to confirm your account. Please sign in again.');
  }

  if (!user?.id) {
    throw new Error('Please sign in to submit a support ticket.');
  }

  return user;
};

export const toAdminSupportTicket = (row) => ({
  id: row.id,
  ticketCode: row.ticket_code,
  issueType: row.issue_type,
  subject: supportTicketSubject(row),
  description: row.description,
  customer: String(row.requester_name || '').trim() || String(row.requester_email || '').trim() || '—',
  email: String(row.requester_email || '').trim(),
  source: row.source,
  status: row.status,
  adminNotes: row.admin_notes || '',
  paymentOrderId: row.payment_order_id || null,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const matchesAdminSupportSearch = (ticket, needle) => {
  const query = String(needle || '').trim().toLowerCase();
  if (!query) return true;

  return [ticket.ticketCode, ticket.subject, ticket.customer, ticket.email, ticket.paymentOrderId, ticket.issueType]
    .some((value) => String(value || '').toLowerCase().includes(query));
};

export const filterAdminSupportTickets = (tickets = [], { search = '', status = 'All', source } = {}) => (
  tickets.filter((ticket) => {
    const matchSource = !source || ticket.source === source;
    const matchStatus = status === 'All' || ticket.status === status;
    return matchSource && matchStatus && matchesAdminSupportSearch(ticket, search);
  })
);

export const listMySupportTickets = async () => {
  const user = await requireSignedInUser();
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(formatSupportTicketError(error, 'Unable to load your support tickets.'));
  }

  return Array.isArray(data) ? data : [];
};

const insertSupportTicket = async (row) => (
  supabase
    .from('support_tickets')
    .insert(row)
    .select('*')
    .single()
);

export const createSupportTicket = async ({
  issueType,
  subject = '',
  description,
  source,
  paymentOrderId = null,
} = {}) => {
  const trimmedType = String(issueType || '').trim();
  const trimmedDescription = String(description || '').trim();
  const trimmedSubject = String(subject || '').trim();
  const trimmedSource = String(source || '').trim();
  const trimmedOrderId = String(paymentOrderId || '').trim() || null;

  if (!trimmedType) {
    throw new Error('Please select an issue type.');
  }

  if (!trimmedDescription) {
    throw new Error('Please describe the issue.');
  }

  if (trimmedSource !== SUPPORT_TICKET_SOURCES.BOOKING_APP && trimmedSource !== SUPPORT_TICKET_SOURCES.WEBSITE) {
    throw new Error('Invalid support ticket source.');
  }

  if (isRefundRequestType(trimmedType) && !trimmedOrderId) {
    throw new Error('Please select the order you want refunded.');
  }

  const user = await requireSignedInUser();
  const requester = await resolveRequesterProfile(user);

  const payload = {
    ticket_code: generateSupportTicketCode(),
    user_id: user.id,
    requester_email: requester.email || null,
    requester_name: requester.name || null,
    issue_type: trimmedType,
    subject: trimmedSubject || trimmedType,
    description: trimmedDescription,
    source: trimmedSource,
    status: SUPPORT_TICKET_STATUSES.OPEN,
    admin_notes: null,
    payment_order_id: isRefundRequestType(trimmedType) ? trimmedOrderId : null,
  };

  let { data, error } = await insertSupportTicket(payload);

  if (error && String(error.code || '') === '23505') {
    const message = String(error.message || '').toLowerCase();
    if (message.includes('support_tickets_one_open_refund_per_order') || message.includes('payment_order_id')) {
      throw new Error('You already have an open refund request for this order.');
    }
    payload.ticket_code = generateSupportTicketCode();
    ({ data, error } = await insertSupportTicket(payload));
  }

  if (error) {
    if (String(error.code || '') === '23505') {
      throw new Error('You already have an open refund request for this order.');
    }
    throw new Error(formatSupportTicketError(error));
  }

  return data;
};

export const listAllSupportTickets = async () => {
  const { rows } = await listSupportTicketsPage({ page: 1, pageSize: 1000 });
  return rows;
};

export const listSupportTicketsPage = async ({
  page = 1,
  pageSize = 10,
  source,
  status = 'All',
} = {}) => {
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || 10);
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;

  let query = supabase
    .from('support_tickets')
    .select('*', { count: 'exact' })
    .order('updated_at', { ascending: false });

  if (source) query = query.eq('source', source);
  if (status && status !== 'All') query = query.eq('status', status);

  const { data, error, count } = await query.range(from, to);
  if (error) {
    throw new Error(formatSupportTicketError(error, 'Unable to load support tickets.'));
  }

  return {
    rows: (Array.isArray(data) ? data : []).map(toAdminSupportTicket),
    count: Number(count) || 0,
  };
};

export const listMySupportTicketsPage = async ({ page = 1, pageSize = 10 } = {}) => {
  const user = await requireSignedInUser();
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || 10);
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;

  const { data, error, count } = await supabase
    .from('support_tickets')
    .select('*', { count: 'exact' })
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) {
    throw new Error(formatSupportTicketError(error, 'Unable to load your support tickets.'));
  }

  return {
    rows: Array.isArray(data) ? data : [],
    count: Number(count) || 0,
    hasMore: from + (data || []).length < Number(count || 0),
  };
};

export const updateSupportTicketStatus = async (id, { status, adminNotes } = {}) => {
  const nextStatus = String(status || '').trim();
  if (!Object.values(SUPPORT_TICKET_STATUSES).includes(nextStatus)) {
    throw new Error('Choose Open, Pending, or Resolved.');
  }

  if (!id) {
    throw new Error('Missing support ticket id.');
  }

  const patch = { status: nextStatus };
  if (adminNotes !== undefined) {
    patch.admin_notes = String(adminNotes || '').trim() || null;
  }

  const { data, error } = await supabase
    .from('support_tickets')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(formatSupportTicketError(error, 'Unable to update this support ticket.'));
  }

  return toAdminSupportTicket(data);
};
